import React, { Fragment, useEffect, useState } from 'react';

import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';

import IconClose from 'assets/images/close.svg';
// import { ReactComponent as IconInviteModal } from 'assets/images/invite_modal.svg';
import { ReactComponent as IconSuccess } from 'assets/images/success.svg';
import { IMAGE_URL } from 'common/constants';
import { validateEmail } from 'common/helpers';
import { RequestStatusEnum } from 'common/types';
import { InvalidText } from 'components/Typography';
import { selectErrors } from 'modules/business/slice';
import ACTIONS from 'modules/rootActions';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useBusiness from '../../hooks/useBusiness';
import Modal from 'components/Modal';
import LottieAnimation from 'components/LottieAnimation';
import inviteDriverLottie from 'assets/json/icon-invite-driver.json';

/**
 * ModalInvite Component
 *
 * Backend error response (422) for already-invited emails:
 * {
 *   httpCode: 422,
 *   errorCode: {
 *     code: "already_invited_can_resend",
 *     message: "This email has already been invited...",
 *     canResend: true,
 *     pendingInvites: [{ email: "user@example.com", inviteId: 123, isExpired: false }]
 *   },
 *   message: "This email has already been invited..."
 * }
 *
 * Note: Keys are camelCased by axios-case-converter middleware.
 */

interface Props {
  handleClose: () => void;
  isModalOpen: boolean;
}

function ModalInvite({ handleClose, isModalOpen }: Props) {
  const dispatch = useAppDispatch();
  const { inviteDriverStatus, inviteDriverResponse, resendInviteStatus } = useBusiness();
  const errors = useAppSelector(selectErrors);
  const [emails, setEmails] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const existingMemberCount = inviteDriverResponse?.existingMemberCount ?? 0;
  const newInviteeCount = inviteDriverResponse?.newInviteeCount ?? 0;
  const hasNewInvitees = newInviteeCount > 0;
  const hasMixedInviteResults = hasNewInvitees && existingMemberCount > 0;

  const isMobile = useMediaQuery({
    query: '(max-width: 500px)',
  });

  useEffect(() => {
    if (inviteDriverStatus === RequestStatusEnum.SUCCESS) {
      dispatch({ type: ACTIONS.FETCH_BUSINESS_INVITEES, payload: { limit: 10, page: 1, sort: 'date_invited', order: 'desc' } });
    }
  }, [inviteDriverStatus]);

  useEffect(() => {
    if(!isModalOpen) setInput('')
  }, [isModalOpen])

  const handleInvite = () => {
    if (emails.length !== 0 || isValidEmail(input)) {
      console.log(isValidEmail(input));
      const includeInput: string[] = [...emails];
      if (input.trim().length > 0) includeInput.push(input.trim());
      const payload = {
        emails: includeInput,
      };
      dispatch({ type: ACTIONS.INVITE_DRIVERS, payload });
    }
  };

  const handleQuickResend = (email: string) => {
    const payload = {
      email: email,
    };
    dispatch({ type: ACTIONS.RESEND_DRIVER_INVITE, payload });
    // Clear error and form after initiating resend
    setEmails([]);
    setInput('');
    setTimeout(() => {
      dispatch({ type: ACTIONS.FETCH_BUSINESS_INVITEES, payload: { limit: 10, page: 1, sort: 'date_invited', order: 'desc' } });
    }, 1000);
  };

  const handleResendAllInvited = () => {
    const alreadyInvitedEmails = getAlreadyInvitedEmails();

    alreadyInvitedEmails.forEach((email: string) => {
      const payload = {
        email: email,
      };
      dispatch({ type: ACTIONS.RESEND_DRIVER_INVITE, payload });
    });

    // Clear form and refresh list after all resends
    setTimeout(() => {
      setEmails([]);
      setInput('');
      dispatch({ type: ACTIONS.FETCH_BUSINESS_INVITEES, payload: { limit: 10, page: 1, sort: 'date_invited', order: 'desc' } });
    }, 1000);
  };

  const handleSendNewOnly = () => {
    const alreadyInvitedEmails = getAlreadyInvitedEmails();

    // Filter out already invited emails
    const newEmails = [...emails];
    if (input.trim().length > 0 && isValidEmail(input)) {
      newEmails.push(input.trim());
    }

    const filteredEmails = newEmails.filter(
      email => !alreadyInvitedEmails.some((inv: string) => inv.toLowerCase() === email.toLowerCase())
    );

    if (filteredEmails.length > 0) {
      const payload = {
        emails: filteredEmails,
      };
      dispatch({ type: ACTIONS.INVITE_DRIVERS, payload });
    }
  };

  const getNewEmailsCount = () => {
    const alreadyInvitedEmails = getAlreadyInvitedEmails();

    const allEmails = [...emails];
    if (input.trim().length > 0 && isValidEmail(input)) {
      allEmails.push(input.trim());
    }

    return allEmails.filter(
      email => !alreadyInvitedEmails.some((inv: string) => inv.toLowerCase() === email.toLowerCase())
    ).length;
  };

  const getAlreadyInvitedEmails = (): string[] => {
    // Extract emails from the backend's pendingInvites array (inside errorCode)
    const pendingInvites = errors?.errorCode?.pendingInvites;
    if (Array.isArray(pendingInvites) && pendingInvites.length > 0) {
      return pendingInvites.map((inv: { email: string }) => inv.email);
    }

    // Fallback: parse email addresses from error message
    if (errors?.message) {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
      const foundEmails = errors.message.match(emailRegex);
      if (foundEmails) {
        const currentEmails = [...emails];
        if (input.trim().length > 0) currentEmails.push(input.trim());
        return foundEmails.filter((email: string) =>
          currentEmails.some(e => e.toLowerCase() === email.toLowerCase())
        );
      }
    }

    return [];
  };

  const isValidEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const renderModalContent = () => {
    if (inviteDriverStatus === RequestStatusEnum.SUCCESS) {
      return (
        <Fragment>
          <IconSuccess className="icon" />
          <h1 className="title form-control text-center text-[20px] font-bold md:text-[34px]">
            {hasNewInvitees ? (hasMixedInviteResults ? 'Invites Sent!' : 'Sending Invitation!') : 'Driver Added!'}
          </h1>
          <p className=" mt-[20px] self-center text-center text-[16px] md:text-[18px]">
            {hasNewInvitees
              ? hasMixedInviteResults
                ? `${newInviteeCount} invitation${newInviteeCount > 1 ? 's are' : ' is'} being sent. ${existingMemberCount} existing member${existingMemberCount > 1 ? 's were' : ' was'} added to your business account right away.`
                : 'Your driver invitation is pending. Link and instructions will be sent to their email to proceed.'
              : 'The driver has been successfully added to your business account.'}
          </p>
        </Fragment>
      );
    }

    const handleEnter = (e: any) => {
      if (
        (e.key === 'Enter' || e.key === ' ') &&
        validateEmail(e.target.value)
      ) {
        const newArr: any = [...emails];
        newArr.push(e.target.value);
        setEmails(newArr);
        setInput('');
      }
      if (e.key === 'Enter' && e.target.value === '' && emails.length > 0) {
        handleInvite();
      }
    };

    const handleRemoveEmail = (index: number) => {
      const newArr: any = [...emails];
      newArr.splice(index, 1);
      setEmails(newArr);
    };

    return (
      <Fragment>
        <LottieAnimation lottieFile={inviteDriverLottie} isStopped={!isModalOpen} />
        <h1 className="title form-control text-center text-[20px] md:text-4xl font-bold">
          Invite Driver
        </h1>
        <p className="mt-[30px] mb-[25px] self-center text-center text-[16px] md:text-[18px]">
          Invite your collegues to become a driver on your business account.
          <br />
          <br />
          Their booking and membership costs will be charged to your default
          payment&nbsp;method.
        </p>
        <label
          htmlFor="input_field"
          className="scrollbar flex h-[150px] w-full flex-wrap gap-[10px] overflow-y-auto rounded-md border-[1px] p-[15px]"
        >
          {emails.map((email, i) => {
            return (
              <label
                htmlFor=""
                key={i}
                className="hide-scrollbar relative flex h-fit items-center gap-[5px] overflow-x-auto rounded-full bg-zinc-100 p-[5px] px-[10px] text-[12px]"
              >
                <p>{email}</p>
                <span
                  className="z-[100] max-h-[8px] min-h-[8px] min-w-[8px] max-w-[8px] cursor-pointer bg-center bg-no-repeat fill-zinc-700"
                  style={{ backgroundImage: `url(${IMAGE_URL}${IconClose})` }}
                  onClick={() => handleRemoveEmail(i)}
                />
              </label>
            );
          })}
          <input
            type="text"
            name="input_field"
            id="input_field"
            placeholder="Enter email address"
            onKeyDown={handleEnter}
            value={input}
            onChange={(e) => setInput(e.target.value.replace(' ', ''))}
            className="h-[30px] flex-grow-[1] cursor-default text-[12px] outline-none"
          />
        </label>
        {errors?.message && (
          <div className="mt-[15px] p-[15px] bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="mb-2">
              <InvalidText>{errors?.message}</InvalidText>
            </div>

            {/* Show resend options when backend indicates already-invited emails */}
            {getAlreadyInvitedEmails().length > 0 && (
              <div className="mt-3">
                <div className="text-sm">
                  {getAlreadyInvitedEmails().length === 1 ? (
                    <p className="text-gray-600 mt-2">
                      This email already has a pending invitation.
                      <button
                        className="ml-2 text-flexi-orange-1 font-semibold hover:underline disabled:opacity-50"
                        onClick={() => handleQuickResend(getAlreadyInvitedEmails()[0])}
                        disabled={resendInviteStatus === RequestStatusEnum.PENDING}
                      >
                        {resendInviteStatus === RequestStatusEnum.PENDING
                          ? 'Resending...'
                          : 'Resend invitation?'}
                      </button>
                    </p>
                  ) : (
                    <>
                      <p className="font-semibold text-gray-700 mb-2">
                        {getAlreadyInvitedEmails().length} email{getAlreadyInvitedEmails().length > 1 ? 's' : ''} already invited:
                      </p>
                      <ul className="list-none text-gray-600 text-xs ml-0 space-y-1">
                        {getAlreadyInvitedEmails().slice(0, 3).map((email: string, idx: number) => (
                          <li key={idx} className="flex items-center justify-between py-1">
                            <span>• {email}</span>
                          </li>
                        ))}
                        {getAlreadyInvitedEmails().length > 3 && (
                          <li className="text-gray-500 italic">...and {getAlreadyInvitedEmails().length - 3} more</li>
                        )}
                      </ul>
                      <button
                        className="mt-2 text-sm text-flexi-orange-1 font-semibold hover:underline disabled:opacity-50"
                        onClick={handleResendAllInvited}
                        disabled={resendInviteStatus === RequestStatusEnum.PENDING}
                      >
                        {resendInviteStatus === RequestStatusEnum.PENDING
                          ? 'Resending...'
                          : 'Resend all invites?'}
                      </button>
                    </>
                  )}
                </div>

                {getNewEmailsCount() === 0 && (
                  <p className="text-xs text-gray-500 italic mt-3">
                    All emails have already been invited.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        <button
          className={classNames(
            `btn mt-[50px] flex !h-[50px] w-full justify-center rounded-full border-none bg-flexi-green-1 text-[16px] capitalize hover:bg-flexi-green-2 disabled:text-white md:!h-[60px]`,
            {
              'loading bg-flexi-green-2':
                inviteDriverStatus === RequestStatusEnum.PENDING,
            },
          )}
          onClick={
            errors?.message && getAlreadyInvitedEmails().length > 0 && getNewEmailsCount() > 0
              ? handleSendNewOnly
              : handleInvite
          }
          disabled={emails.length === 0 && !isValidEmail(input)}
        >
          <p>
            {errors?.message && getAlreadyInvitedEmails().length > 0 && getNewEmailsCount() > 0
              ? `Invite ${getNewEmailsCount()} new driver${getNewEmailsCount() > 1 ? 's' : ''} only`
              : 'Invite'}
          </p>
        </button>
      </Fragment>
    );
  };

  return (
    <Modal
      handleClose={() => {
        handleClose();
        setEmails([]);
      }}
      isModalOpen={isModalOpen}
      px={isMobile ? 'px-[25px]' : 'px-[120px]'}
      mx={isMobile ? '30px' : '0px'}
    >
      {renderModalContent()}
    </Modal>
  );
}

export default ModalInvite;
